/**
 * PIQ 9.0: Decision Section (Decision Compression)
 * =================================================
 * SINGLE DECISION STACK - exactly ONE of:
 * - ACT: Top 1–3 actions (ROI-sorted, max 3)
 * - PREPARE: One playbook
 * - HOLD: Decisive explanation (why, what monitored, what triggers action)
 * - CLEAR: "All clear" explanation
 * 
 * INVARIANT: Only one stack renders. No placeholders, no "AI analyzing..."
 */

import React, { useEffect } from 'react';
import {
  Zap,
  Shield,
  CheckCircle,
  ChevronRight,
  DollarSign,
  Activity,
  Brain,
  Target,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import type { ActionItem, PostureType, SourceType } from './operatorViewAdapter';
import type { InsightDetailVM } from './InsightDetailDrawer';

// PIQ 9.0: Extended BrainContract for HOLD explanation
interface BrainContractSubset {
  decisionPosture?: { posture: PostureType };
  businessState?: { primaryState?: string; stateLabel?: string; confidence?: number };
  beliefSummary?: { primary: string | null; weight?: number };
  computedExpectedValue?: { metricsEvaluated?: number; totalUnexpectedDelta?: number };
  earlyWarnings?: { active?: Array<{ id: string; type: string }> };
  retailerAttribution?: { primaryDriver?: { retailer: string; contributionPct: number } };
  microAttribution?: Record<string, { topSkus?: any[] }>;
}

// ============================================================
// Types
// ============================================================

/** PIQ 9.0: Single decision stack type */
export type DecisionStack = 'act' | 'prepare' | 'hold' | 'clear';

interface DecisionSectionProps {
  brainContract: BrainContractSubset;
  posture: PostureType;
  /** PIQ 9.0: Explicit stack - determines which section renders */
  decisionStack: DecisionStack;
  actions: ActionItem[];
  containmentPlaybook?: any;
  preventivePlaybook?: any;
  onActionClick?: (item: ActionItem) => void;
  onConvertPlaybook?: (id: string) => void;
  onOpenDetail?: (detail: InsightDetailVM) => void;
}

/** PIQ 9.0: Max actions enforced */
const MAX_ACTIONS = 3;
const MIN_ROI_THRESHOLD = 40;

// ============================================================
// Source Badge
// ============================================================

const SOURCE_BADGE_CONFIG: Record<SourceType, { label: string; className: string }> = {
  piq: { label: 'PIQ', className: 'bg-violet-100 text-violet-700' },
  google: { label: 'Google', className: 'bg-blue-100 text-blue-700' },
  meta: { label: 'Meta', className: 'bg-sky-100 text-sky-700' },
  forecast: { label: 'Forecast', className: 'bg-gray-100 text-gray-700' },
  pattern: { label: 'Pattern', className: 'bg-indigo-100 text-indigo-700' },
  manual: { label: 'Manual', className: 'bg-amber-100 text-amber-700' },
  system: { label: 'System', className: 'bg-slate-100 text-slate-700' },
};

function SourceBadge({ sourceType }: { sourceType: SourceType }) {
  const config = SOURCE_BADGE_CONFIG[sourceType] || SOURCE_BADGE_CONFIG.piq;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

// ============================================================
// Helper: Map action to detail VM
// ============================================================

function mapActionToDetailVM(item: ActionItem): InsightDetailVM {
  return {
    id: item.id,
    title: item.title,
    category: 'drivers',
    categoryLabel: 'Action',
    confidence: item.roiScore / 100,
    impactBand: item.expectedImpact || null,
    why: item.description,
    evidenceRefs: [],
    context: { state: item.retailer },
  };
}

// ============================================================
// PIQ 9.0: ACT Section - Hard-limited to 3 actions
// ============================================================

function ActSection({ 
  actions, 
  onActionClick, 
  onOpenDetail 
}: { 
  actions: ActionItem[]; 
  onActionClick?: (item: ActionItem) => void;
  onOpenDetail?: (detail: InsightDetailVM) => void;
}) {
  // PIQ 9.0 Task 3: Hard limit to MAX_ACTIONS, sorted by ROI
  const sortedActions = [...actions]
    .filter(a => a.roiScore >= MIN_ROI_THRESHOLD)
    .sort((a, b) => b.roiScore - a.roiScore)
    .slice(0, MAX_ACTIONS);
  
  // PIQ 9.0: Runtime invariant - max 3 actions
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && sortedActions.length > MAX_ACTIONS) {
      console.error(`[ActSection] INVARIANT VIOLATED: ${sortedActions.length} actions rendered (max ${MAX_ACTIONS})`);
    }
  }, [sortedActions.length]);
  
  const getRoiColor = (score: number) => {
    if (score >= 80) return 'text-emerald-700 bg-emerald-50';
    if (score >= 60) return 'text-amber-700 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-red-600" />
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Top Actions</h3>
        <span className="text-[10px] text-gray-400">({sortedActions.length} ROI-ranked)</span>
      </div>
      
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {sortedActions.map((action, idx) => (
          <button
            key={action.id}
            onClick={() => {
              if (onOpenDetail) onOpenDetail(mapActionToDetailVM(action));
              else if (onActionClick) onActionClick(action);
            }}
            className="text-left bg-white rounded-lg p-3 border border-red-200/60 hover:border-red-300 hover:shadow-sm transition-all group"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-700 text-[10px] font-bold shrink-0">
                  {idx + 1}
                </span>
                <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{action.title}</h4>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <SourceBadge sourceType={action.provenanceType} />
              </div>
            </div>
            <p className="text-xs text-gray-500 line-clamp-2 mb-2 ml-5">{action.description}</p>
            <div className="flex items-center justify-between text-xs ml-5">
              <div className="flex items-center gap-2 text-gray-500">
                {action.expectedImpact && (
                  <span className="flex items-center gap-0.5 text-emerald-600 font-medium">
                    <DollarSign className="w-3 h-3" />
                    {action.expectedImpact}
                  </span>
                )}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getRoiColor(action.roiScore)}`}>
                  {action.roiLabel}
                </span>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// PREPARE Section
// ============================================================

function PrepareSection({ 
  playbook, 
  type,
  onConvert,
  onOpenDetail,
}: { 
  playbook: any;
  type: 'containment' | 'preventive';
  onConvert?: () => void;
  onOpenDetail?: (detail: InsightDetailVM) => void;
}) {
  const isContainment = type === 'containment';
  const bgClass = isContainment ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200';
  const iconClass = isContainment ? 'text-orange-600' : 'text-blue-600';
  const badgeClass = isContainment ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';

  return (
    <div className={`rounded-lg border p-3 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <Shield className={`w-4 h-4 ${iconClass}`} />
        <h3 className="text-sm font-semibold text-gray-900">{playbook.title || 'Preparation Steps'}</h3>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badgeClass}`}>
          {isContainment ? 'Containment' : 'Preventive'}
        </span>
      </div>
      
      <p className="text-xs text-gray-600 mb-2">{playbook.description}</p>
      
      {playbook.steps?.length > 0 && (
        <ul className="space-y-1 mb-2">
          {playbook.steps.slice(0, 3).map((step: any, idx: number) => (
            <li key={step.id || idx} className="flex items-start gap-2 text-xs text-gray-700">
              <span className={`shrink-0 w-4 h-4 rounded-full ${badgeClass} flex items-center justify-center text-[10px] font-bold`}>
                {idx + 1}
              </span>
              <span>{step.title}</span>
            </li>
          ))}
        </ul>
      )}
      
      {onConvert && (
        <button
          onClick={onConvert}
          className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${
            isContainment 
              ? 'bg-orange-600 text-white hover:bg-orange-700' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          Convert to Actions
        </button>
      )}
    </div>
  );
}

// ============================================================
// PIQ 9.0: HOLD Section - Make "No Action" Feel Decisive
// ============================================================

/**
 * PIQ 9.0 Task 2: Generate decisive HOLD explanation
 * Answers: Why no action? What's monitored? What triggers action?
 * Returns 2-3 sentences (not bullets).
 */
function generateHoldExplanation(brainContract: BrainContractSubset): string {
  const parts: string[] = [];
  
  const stateLabel = brainContract.businessState?.stateLabel || brainContract.businessState?.primaryState;
  const beliefLabel = brainContract.beliefSummary?.primary?.replace(/_/g, ' ');
  const cev = brainContract.computedExpectedValue;
  const metricsEvaluated = cev?.metricsEvaluated ?? 0;
  const unexpectedDelta = cev?.totalUnexpectedDelta ?? 0;
  const earlyWarnings = brainContract.earlyWarnings?.active || [];
  
  // Part 1: WHY no action is needed
  if (metricsEvaluated === 0) {
    parts.push('We are holding because no metrics were evaluable this week (snapshot incomplete or outside window).');
  } else if (Math.abs(unexpectedDelta) < 1000) {
    const stateContext = stateLabel ? ` during ${stateLabel.replace(/_/g, ' ')}` : '';
    parts.push(`We are holding because performance is within expected ranges${stateContext} and no unexpected downside is detected.`);
  } else if (beliefLabel?.includes('normal') || beliefLabel?.includes('stable')) {
    parts.push(`We are holding because the current belief (${beliefLabel}) indicates stable conditions with no material action triggers.`);
  } else {
    parts.push('We are holding because no actions met the ROI threshold this week.');
  }
  
  // Part 2: WHAT is being monitored
  const monitorTargets: string[] = [];
  
  // Check for inventory concerns
  if (brainContract.microAttribution) {
    const keys = Object.keys(brainContract.microAttribution);
    if (keys.length > 0) {
      const retailer = keys[0].charAt(0).toUpperCase() + keys[0].slice(1);
      monitorTargets.push(`${retailer} inventory levels`);
    }
  }
  
  // Check early warnings
  if (earlyWarnings.length > 0) {
    monitorTargets.push(`${earlyWarnings.length} early warning signal${earlyWarnings.length > 1 ? 's' : ''}`);
  }
  
  // Check retailer attribution
  if (brainContract.retailerAttribution?.primaryDriver) {
    const driver = brainContract.retailerAttribution.primaryDriver;
    monitorTargets.push(`${driver.retailer.charAt(0).toUpperCase() + driver.retailer.slice(1)} trends`);
  }
  
  if (monitorTargets.length > 0) {
    parts.push(`We are monitoring ${monitorTargets.slice(0, 2).join(' and ')}.`);
  }
  
  // Part 3: WHAT would trigger action
  const triggers: string[] = [];
  
  if (brainContract.retailerAttribution?.primaryDriver) {
    triggers.push('material unexpected loss above threshold');
  }
  if (metricsEvaluated > 0) {
    triggers.push('WoW decline exceeding 15%');
  }
  triggers.push('new early warning signals');
  
  if (triggers.length > 0) {
    parts.push(`If ${triggers[0]} emerges, this will escalate to action.`);
  }
  
  return parts.join(' ');
}

function HoldSection({ brainContract }: { brainContract: BrainContractSubset }) {
  const explanation = generateHoldExplanation(brainContract);

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/80 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <CheckCircle className="w-4 h-4 text-gray-500" />
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">No Actions Required</h3>
      </div>
      
      {/* PIQ 9.0: Single prose explanation (2-3 sentences) */}
      <p className="text-sm text-gray-600 leading-relaxed">
        {explanation}
      </p>
    </div>
  );
}

// ============================================================
// PIQ 9.0: CLEAR Section - All Clear Explanation
// ============================================================

function ClearSection({ brainContract }: { brainContract: BrainContractSubset }) {
  const stateLabel = brainContract.businessState?.stateLabel || brainContract.businessState?.primaryState;
  
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/80 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <CheckCircle className="w-4 h-4 text-emerald-600" />
        <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">All Clear</h3>
      </div>
      
      <p className="text-sm text-emerald-700 leading-relaxed">
        No material issues detected this week. 
        {stateLabel && ` Business is in ${stateLabel.replace(/_/g, ' ')} state.`}
        {' '}All metrics are within expected ranges and no action is required.
      </p>
    </div>
  );
}

// ============================================================
// PIQ 9.0: Main Component - Single Decision Stack
// ============================================================

export function DecisionSection({
  brainContract,
  posture,
  decisionStack,
  actions,
  containmentPlaybook,
  preventivePlaybook,
  onActionClick,
  onConvertPlaybook,
  onOpenDetail,
}: DecisionSectionProps) {
  // PIQ 9.0: Runtime invariant - verify single stack
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const validStacks: DecisionStack[] = ['act', 'prepare', 'hold', 'clear'];
      if (!validStacks.includes(decisionStack)) {
        console.error(`[DecisionSection] INVARIANT VIOLATED: Invalid decision stack "${decisionStack}"`);
      }
    }
  }, [decisionStack]);
  
  // PIQ 9.0: Render exactly ONE stack based on decisionStack prop
  switch (decisionStack) {
    case 'act':
      return (
        <ActSection 
          actions={actions} 
          onActionClick={onActionClick}
          onOpenDetail={onOpenDetail}
        />
      );
    
    case 'prepare':
      // Containment takes priority over preventive
      const playbook = containmentPlaybook || preventivePlaybook;
      const type = containmentPlaybook ? 'containment' : 'preventive';
      if (playbook) {
        return (
          <PrepareSection
            playbook={playbook}
            type={type}
            onConvert={() => onConvertPlaybook?.(playbook.id)}
            onOpenDetail={onOpenDetail}
          />
        );
      }
      // Fallback to HOLD if no playbook (shouldn't happen)
      return <HoldSection brainContract={brainContract} />;
    
    case 'clear':
      return <ClearSection brainContract={brainContract} />;
    
    case 'hold':
    default:
      return <HoldSection brainContract={brainContract} />;
  }
}

export default DecisionSection;
